// src/views/admin/default/components/AttendanceOverviewCard.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "components/card";
import PieChart from "components/charts/PieChart";
import { CalendarDays } from "lucide-react";
import { getAttendance, getEmployees } from "services/hrms.api";

// Colors to match the mock
const COLORS = {
  present: "#8FFF4E", // bright green
  late: "#0F766E", // teal/dark
  permission: "#F59E0B", // amber
  absent: "#FF734E", // red
};

const REFRESH_MS = 30_000; // 30 seconds – tweak as you like

function Dot({ color }) {
  return (
    <span
      className="mr-2 inline-block h-2 w-2 rounded-full"
      style={{ backgroundColor: color }}
    />
  );
}

// helper to match your DB format "2025-10-30"
function todayWorkDay() {
  return new Date().toISOString().slice(0, 10);
}

export default function AttendanceOverviewCard({
  data: externalData, // optional override
  dateLabel = "Today",
  onDetails,
}) {
  const navigate = useNavigate();

  const [summary, setSummary] = useState({
    present: 0,
    late: 0,
    permission: 0,
    absent: 0,
  });
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    let timerId;

    const fetchSummary = async () => {
      try {
        if (cancelled) return;
        setError("");

        const workDay = todayWorkDay();

        const [{ data: employees }, { data: attendances }] = await Promise.all([
          getEmployees(),
          getAttendance({ workDay }),
        ]);

        if (cancelled) return;

        const allEmployees = employees || [];
        const allAttendances = attendances || [];

        const activeEmployees = allEmployees.filter((e) => {
          if (typeof e.active === "boolean") return e.active;
          const s = String(e.status || "active").toLowerCase();
          return s !== "inactive";
        });

        const totalEmployees = activeEmployees.length;

        const presentIds = new Set(
          allAttendances.map((row) => String(row.employee))
        );
        const present = presentIds.size;

        const late = 0;
        const permission = 0;

        let absent = totalEmployees - present;
        if (absent < 0) absent = 0;

        setSummary({ present, late, permission, absent });
      } catch (e) {
        if (cancelled) return;
        console.error(e);
        setError(e?.message || "Failed to load attendance");
      }
    };

    fetchSummary();
    timerId = setInterval(fetchSummary, REFRESH_MS);

    return () => {
      cancelled = true;
      if (timerId) clearInterval(timerId);
    };
  }, []);

  // Use external data if passed, otherwise use our computed summary
  const data = externalData || summary;

  const total = useMemo(() => {
    const t =
      (data.total ??
        (data.present || 0) +
          (data.late || 0) +
          (data.permission || 0) +
          (data.absent || 0)) ||
      0;
    return t;
  }, [data]);

  const series = useMemo(
    () => [
      data.present || 0,
      data.late || 0,
      data.permission || 0,
      data.absent || 0,
    ],
    [data]
  );

  // ✅ make pct stable; changes only when total changes
  const pct = useCallback(
    (n) => (total ? Math.round((n / total) * 100) : 0),
    [total]
  );

  const options = useMemo(
    () => ({
      labels: ["Present", "Late", "Permission", "Absent"],
      colors: [COLORS.present, COLORS.late, COLORS.permission, COLORS.absent],
      chart: { type: "donut", sparkline: { enabled: true } },
      legend: { show: false },
      dataLabels: { enabled: false },
      stroke: { width: 0 },
      plotOptions: {
        pie: {
          startAngle: -110,
          endAngle: 110,
          expandOnClick: false,
          donut: {
            size: "78%",
            labels: {
              show: true,
              name: { show: false },
              value: {
                show: true,
                fontSize: "28px",
                fontWeight: 700,
                color: "#111827",
                formatter: () => String(total),
              },
              total: {
                show: true,
                label: "Total Attendance",
                fontSize: "12px",
                color: "#6B7280",
              },
            },
          },
        },
      },
      tooltip: {
        y: {
          formatter: (val) => `${val} (${pct(val)}%)`,
        },
      },
    }),
    [total, pct]
  );

  const handleViewDetails = () => {
    if (typeof onDetails === "function") onDetails();
    navigate("/admin/hrms/attendances");
  };

  return (
    <Card extra="rounded-[20px] p-0">
      <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3 dark:border-white/10">
        <h4 className="text-base font-semibold text-navy-700 dark:text-white">
          Attendance Overview
        </h4>
        <button
          type="button"
          className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-white/10"
          title="Jump to today"
        >
          <CalendarDays className="h-4 w-4" />
          {dateLabel}
        </button>
      </div>

      <div className="flex flex-col items-center px-6 pt-6">
        <div className="mb-4 flex h-[240px] w-full items-center justify-center dark:text-white">
          <PieChart options={options} series={series} />
        </div>

        <div className="grid w-full max-w-xs grid-cols-2 gap-y-2 text-sm">
          <div className="flex items-center">
            <Dot color={COLORS.present} />
            <span className="text-gray-700 dark:text-white/80">Present</span>
          </div>
          <div className="text-right text-gray-700 dark:text-white/80">
            {pct(data.present)}%
          </div>

          <div className="flex items-center">
            <Dot color={COLORS.late} />
            <span className="text-gray-700 dark:text-white/80">Late</span>
          </div>
          <div className="text-right text-gray-700 dark:text-white/80">
            {pct(data.late)}%
          </div>

          <div className="flex items-center">
            <Dot color={COLORS.permission} />
            <span className="text-gray-700 dark:text-white/80">Permission</span>
          </div>
          <div className="text-right text-gray-700 dark:text-white/80">
            {pct(data.permission)}%
          </div>

          <div className="flex items-center">
            <Dot color={COLORS.absent} />
            <span className="text-gray-700 dark:text-white/80">Absent</span>
          </div>
          <div className="text-right text-gray-700 dark:text-white/80">
            {pct(data.absent)}%
          </div>
        </div>

        {error && (
          <div className="mt-3 w-full text-center text-xs text-rose-500 dark:text-rose-300">
            {error}
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center justify-between rounded-b-[20px] bg-gray-50 px-5 py-3 text-sm dark:bg-white/5">
        <div className="flex items-center gap-3">
          <span className="font-medium text-gray-700 dark:text-white/80">
            Total Absentees
          </span>
          <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-600 dark:bg-red-900/30 dark:text-red-300">
            {data.absent}
          </span>
        </div>
        <button
          onClick={handleViewDetails}
          className="text-xs font-semibold text-brand-600 hover:underline dark:text-gray-300"
        >
          View Details
        </button>
      </div>
    </Card>
  );
}
